import { PerformanceDatapoint, returnTopExecutingActionDatapoint } from 'owa-analytics';
import {
    CreateCalendarGroupResult,
    default as createCalendarGroupService,
} from '../services/createCalendarGroupService';
import {
    addCalendarGroupToCalendarsCache,
    removeCalendarGroupFromCalendarsCache,
} from 'owa-calendar-cache';
import {
    calendarNotificationsBarCreateCalendarGroup,
    calendarNotificationsBarCreateCalendarGroupFailed,
} from './createCalendarGroup.locstring.json';
import loc, { format } from 'owa-localize';

import type { CalendarGroup } from 'owa-graph-schema';
import { ControlIcons } from 'owa-control-icons';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { getCalendarGroups } from 'owa-calendar-surface-calendars-store';
import { getUserMailboxInfo } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyShowCalendarNotification } from 'owa-calendar-notification-bar';
import { setCalendarGroupDisplayOrder } from './setCalendarGroupDisplayOrder';
import showCalendarErrorNotification from './showCalendarErrorNotification';
import { trace } from 'owa-trace';
import { tryAgainLabel } from 'owa-locstrings/lib/strings/tryagainlabel.locstring.json';

/**
 * Action to create calendar group
 */
export let createCalendarGroup = createLegacyActionWithDatapoint('CreateCalendarGroup', {
    customData: (userIdentity: string, groupName: string) => {
        return { rejected: !groupName || groupName.trim().length == 0 };
    },
})(async function createCalendarGroupInternal(
    userIdentity: string,
    groupName: string
): Promise<void> {
    if (!groupName || groupName.trim().length == 0) {
        return;
    }
    const dataPoint = returnTopExecutingActionDatapoint();

    let newCalendarGroup = instantCreateCalendarGroup(userIdentity, groupName);

    const response = await executeCreateCalendarGroupRequest(
        newCalendarGroup,
        dataPoint,
        groupName,
        userIdentity
    );

    if (!response) {
        onRequestFailed(userIdentity, newCalendarGroup, groupName);
    }
});

async function executeCreateCalendarGroupRequest(
    newCalendarGroup: CalendarGroup,
    dataPoint: PerformanceDatapoint,
    groupName: string,
    userIdentity: string
) {
    // Add calendar group instantly. It will be rolled back if action fails
    if (isFeatureEnabled('cal-calendarPickerReorder')) {
        addCalendarGroupAtIndex(newCalendarGroup, userIdentity);
    } else {
        getCalendarGroups().push(newCalendarGroup);
    }

    if (dataPoint) {
        dataPoint.markUserPerceivedTime(); // mark user perceived time here as soon as we dispatch the request to create
    }

    lazyShowCalendarNotification.import().then(showCalendarNotification => {
        showCalendarNotification(
            'createCalendarGroup',
            'Surface',
            format(loc(calendarNotificationsBarCreateCalendarGroup), groupName),
            { icon: ControlIcons.CheckMark }
        );
    });

    try {
        const response = await createCalendarGroupService(
            groupName,
            getUserMailboxInfo(userIdentity)
        );
        processServiceResponse(response, newCalendarGroup);
        return response;
    } catch (error) {
        return null;
    }
}

function onRequestFailed(userIdentity: string, newCalendarGroup: CalendarGroup, groupName: string) {
    let calendarIndex = findInArray(getCalendarGroups(), item => {
        return (
            item.calendarGroupId.id == newCalendarGroup.calendarGroupId.id &&
            item.calendarGroupId.mailboxInfo.userIdentity == userIdentity
        );
    });

    if (calendarIndex.index >= 0) {
        getCalendarGroups().splice(calendarIndex.index, 1);
    } else {
        trace.warn(
            `[renameCalendar] Cannot find calendar id: ${newCalendarGroup.calendarGroupId.id}`
        );
    }

    removeCalendarGroupFromCalendarsCache(newCalendarGroup);

    showCalendarErrorNotification(
        'createCalendarGroup',
        format(loc(calendarNotificationsBarCreateCalendarGroupFailed), groupName),
        ControlIcons.Error,
        loc(tryAgainLabel),
        () => createCalendarGroup(userIdentity, groupName)
    );
}

function instantCreateCalendarGroup(userIdentity: string, groupName: string): CalendarGroup {
    const mailboxInfo = getUserMailboxInfo(userIdentity);
    const newCalendarGroupId = Math.random().toString();
    const newCalendarGroupServerId = Math.random().toString();
    let calendarGroup = {
        calendarGroupId: {
            id: newCalendarGroupId,
            mailboxInfo: mailboxInfo,
        },
        serverGroupId: newCalendarGroupServerId,
        GroupName: groupName,
    };
    addCalendarGroupToCalendarsCache(calendarGroup);
    return calendarGroup;
}

function processServiceResponse(
    response: CreateCalendarGroupResult,
    newCalendarGroup: CalendarGroup
): CalendarGroup {
    removeCalendarGroupFromCalendarsCache(newCalendarGroup);
    if (response) {
        newCalendarGroup.calendarGroupId = {
            id: response.newCalendarGroupItemId.Id,
            changeKey: response.newCalendarGroupItemId.ChangeKey,
            mailboxInfo: newCalendarGroup.calendarGroupId.mailboxInfo,
        };
        newCalendarGroup.serverGroupId = response.serverGroupId;

        addCalendarGroupToCalendarsCache(newCalendarGroup);

        if (isFeatureEnabled('cal-calendarPickerReorder')) {
            setCalendarGroupDisplayOrder(
                newCalendarGroup.calendarGroupId.mailboxInfo.userIdentity,
                newCalendarGroup.serverGroupId
            );
        }
        trace.info(
            `[createCalendarGroup] Created a new calendar group on server: ${newCalendarGroup.GroupName}`
        );
    }

    return null;
}

function addCalendarGroupAtIndex(newCalendarGroup: CalendarGroup, userIdentity: string) {
    const filteredGroups: CalendarGroup[] = getCalendarGroups().filter(
        group =>
            group.calendarGroupId.mailboxInfo.type === 'UserMailbox' &&
            group.calendarGroupId.mailboxInfo.userIdentity == userIdentity
    );

    // There is always at least one calendar of type UserMailbox.
    if (filteredGroups.length > 0) {
        const lastGroup = filteredGroups[filteredGroups.length - 1];
        let calendarIndex = findInArray(getCalendarGroups(), item => {
            return (
                item.calendarGroupId.id == lastGroup.calendarGroupId.id &&
                item.calendarGroupId.mailboxInfo.userIdentity == userIdentity
            );
        });

        if (calendarIndex) {
            getCalendarGroups().splice(calendarIndex.index + 1, 0, newCalendarGroup);
        } else {
            trace.warn(
                `[createCalendarGroup] Cannot find calendar group id: ${lastGroup.calendarGroupId.id}`
            );
        }
    }
}

export default createCalendarGroup;
